/**
 * Skids Discipline Configuration
 * 
 * This configuration defines all aspects of the Skids matrix:
 * - Column definitions
 * - Field mappings (frontend <-> backend)
 * - API endpoints
 * - Permissions
 * - Available features
 * 
 * Note: Skids is a mechanical sub-table. It has its own:
 * - Template
 * - Rules of Credit table (rules_of_credit_skids)
 * - Column configuration
 * 
 * Progress steps: Received, Layout, Grout, Rough Set, Final Set
 */

export const FIELD_MAP = {
  buildingNumber: 'building_number',
  sovCode: 'sov_code',
  wbsCode: 'wbs_code',
  area: 'area',
  subArea: 'sub_area',
  elevation: 'elevation',
  floor: 'floor',
  equipmentTagOrCode: 'equipment_tag_or_code',
  description: 'description',
  equipmentType: 'equipment_type',
  equipment_type: 'equipment_type',
  budgetedHr: 'budgeted_hr',
  credit: 'credit',
  costCode: 'cost_code',
  dateEquipmentReceived: 'date_equipment_received',
  drawingNumber: 'drawing_number',
  pAndId: 'p_and_id',
  divisionArea: 'division_area',
  subDivisionArea: 'sub_division_area',
  activityId: 'activity_id',
  activityName: 'activity_name',
  received: 'received',
  layout: 'layout',
  grout: 'grout',
  roughSet: 'rough_set',
  finalSet: 'final_set',
  comments: 'comments',
  udf01: 'udf01',
  udf02: 'udf02',
  udf03: 'udf03',
  udf04: 'udf04',
  udf05: 'udf05',
  udf06: 'udf06',
  udf07: 'udf07',
  udf08: 'udf08',
  udf09: 'udf09',
  udf10: 'udf10',
};

export const FIELD_COMPLETION_COLUMNS = [
  'received',
  'layout',
  'grout',
  'roughSet',
  'finalSet',
];

export const EDITABLE_COLUMNS = [
  'activityId', 'activityName',
  'buildingNumber', 'sovCode', 'wbsCode', 'budgetedHr', 'credit', 'costCode',
  'dateEquipmentReceived', 'equipmentType',
  'pAndId', 'divisionArea', 'subDivisionArea',
  'received', 'layout', 'grout', 'roughSet', 'finalSet',
  'comments', 'udf01', 'udf02', 'udf03', 'udf04', 'udf05',
  'udf06', 'udf07', 'udf08', 'udf09', 'udf10',
];

export const transformMatrixData = (matrixData) => {
  return matrixData.map((row, index) => {
    const transformed = {
      id: row.id || index,
      area: row.area || '',
      subArea: row.sub_area || '',
      buildingNumber: row.building_number || '',
      sovCode: row.sov_code || '',
      sovDescription: row.sov_description || '',
      wbsCode: row.wbs_code || '',
      floor: row.floor || '',
      divisionArea: row.division_area || '',
      subDivisionArea: row.sub_division_area || '',
      jobNumber: row.job_number || '',
      activityId: row.activity_id || '',
      activityName: row.activity_name || '',
      costCode: row.cost_code || '',
      elevation: row.elevation || '',
      pAndId: row.p_and_id || '',
      drawingNumber: row.drawing_number || '',
      equipmentTagOrCode: row.equipment_tag_or_code || '',
      description: row.description || '',
      equipmentType: row.equipment_type || '',
      dateEquipmentReceived: row.date_equipment_received || '',
      received: row.received ? Math.round(row.received) : 0,
      layout: row.layout ? Math.round(row.layout) : 0,
      grout: row.grout ? Math.round(row.grout) : 0,
      roughSet: row.rough_set ? Math.round(row.rough_set) : 0,
      finalSet: row.final_set ? Math.round(row.final_set) : 0,
      totalFieldCompletion: row.total_field_completion ? Math.round(row.total_field_completion) : 0,
      budgetedHr: row.budgeted_hr ? Number(row.budgeted_hr) : 0,
      credit: row.credit ? Number(row.credit) : 0,
      earnedHr: row.earned_hr ? Number(row.earned_hr) : 0,
      earnedCredit: row.earned_credit ? Number(row.earned_credit) : 0,
      createdAt: row.created_at || '',
      updatedAt: row.updated_at || '',
      comments: row.comments || '',
    };
    
    for (let i = 1; i <= 10; i++) {
      const udfKey = `udf${String(i).padStart(2, '0')}`;
      transformed[udfKey] = row[udfKey] || '';
    }
    
    return transformed;
  });
};

export const buildChangePayload = (rowId, columnId, oldValue, newValue) => {
  const backendField = FIELD_MAP[columnId];
  if (!backendField) return null;
  return { id: rowId, changes: { [backendField]: newValue } };
};

export const skidsConfig = {
  id: 'skids',
  name: 'Skids',
  menuId: 'mechanical-skids',

  // Pin the selection checkbox + Equipment Tag + Description columns on horizontal scroll
  frozenColumnIds: ['equipmentTagOrCode', 'description'],
  freezeActionColumns: true,
  tableName: 'skids_tracking',
  historyTableName: 'skids_tracking_history',
  
  api: {
    getData: '/mechanical/skids/project/{projectId}',
    saveChanges: '/mechanical/skids/save-changes',
    export: '/mechanical/skids/export/{projectId}',
    upload: '/mechanical/skids/upload-parsed',
    template: '/mechanical/skids/template/{projectId}',
    priorities: '/priorities',
    columnMappings: '/mechanical/skids/project/{projectId}/column-mappings',
    progressHistory: '/mechanical/skids/{matrixId}/progress-history',
  },
  
  permissions: {
    edit: 'pt_edit',
    analytics: 'pt_analytics',
    import: 'pt_import',
    export: 'pt_export',
    template: 'pt_template',
    columnConfig: 'pt_columnconfig',
    roc: 'pt_roc',
    sfRatio: 'pt_sfratio',
    matBore: 'pt_matbore',
    delete: 'pt_delete',
  },
  
  columns: [
    // === VISIBLE COLUMNS ===
    { id: 'drawingNumber', header: 'Drawing Number', accessor: 'drawingNumber', dbField: 'drawing_number', width: 100, editable: false, filterable: true, type: 'text', showInDashboard: true },
    { id: 'area', header: 'Area', accessor: 'area', dbField: 'area', width: 80, editable: false, filterable: true, type: 'text', showInDashboard: true },
    { id: 'subArea', header: 'Sub Area', accessor: 'subArea', dbField: 'sub_area', width: 80, editable: false, filterable: true, type: 'text', showInDashboard: true },
    { id: 'elevation', header: 'Elevation', accessor: 'elevation', dbField: 'elevation', width: 70, editable: false, filterable: true, type: 'text', showInDashboard: true },
    { id: 'floor', header: 'Floor', accessor: 'floor', dbField: 'floor', width: 60, editable: false, filterable: true, type: 'text', showInDashboard: true },
    { id: 'equipmentTagOrCode', header: 'Equipment Tag/Code', accessor: 'equipmentTagOrCode', dbField: 'equipment_tag_or_code', width: 120, editable: false, filterable: true, type: 'text', showInDashboard: true },
    { id: 'description', header: 'Description', accessor: 'description', dbField: 'description', width: 150, editable: false, filterable: true, type: 'text', showInDashboard: true },
    { id: 'dateEquipmentReceived', header: 'Date Equipment Received', accessor: 'dateEquipmentReceived', dbField: 'date_equipment_received', width: 110, editable: true, filterable: true, type: 'date', showInDashboard: true },
    // Progress step columns
    { id: 'received', header: 'Received', accessor: 'received', dbField: 'received', width: 80, editable: true, filterable: true, type: 'progress', options: [0, 25, 50, 75, 100], affectsCalculation: 'totalFieldCompletion', headerColor: '#E8F5E9', headerGroup: 'Field Progress', showInDashboard: true },
    { id: 'layout', header: 'Layout', accessor: 'layout', dbField: 'layout', width: 80, editable: true, filterable: true, type: 'progress', options: [0, 25, 50, 75, 100], affectsCalculation: 'totalFieldCompletion', headerColor: '#E8F5E9', headerGroup: 'Field Progress', showInDashboard: true },
    { id: 'grout', header: 'Grout', accessor: 'grout', dbField: 'grout', width: 80, editable: true, filterable: true, type: 'progress', options: [0, 25, 50, 75, 100], affectsCalculation: 'totalFieldCompletion', headerColor: '#E8F5E9', headerGroup: 'Field Progress', showInDashboard: true },
    { id: 'roughSet', header: 'Rough Set', accessor: 'roughSet', dbField: 'rough_set', width: 80, editable: true, filterable: true, type: 'progress', options: [0, 25, 50, 75, 100], affectsCalculation: 'totalFieldCompletion', headerColor: '#E8F5E9', headerGroup: 'Field Progress', showInDashboard: true },
    { id: 'finalSet', header: 'Final Set', accessor: 'finalSet', dbField: 'final_set', width: 80, editable: true, filterable: true, type: 'progress', options: [0, 25, 50, 75, 100], affectsCalculation: 'totalFieldCompletion', headerColor: '#E8F5E9', headerGroup: 'Field Progress', showInDashboard: true },
    { id: 'totalFieldCompletion', header: 'Total Completion', accessor: 'totalFieldCompletion', dbField: 'total_field_completion', width: 90, editable: false, filterable: true, type: 'progress-readonly', computed: true, headerColor: '#C8E6C9', headerGroup: 'Field Progress', showInDashboard: true },
    { id: 'comments', header: 'Comments', accessor: 'comments', dbField: 'comments', width: 150, editable: true, filterable: true, type: 'text', showInDashboard: true },
    
    // === HIDDEN COLUMNS (available for import/export and column config) ===
    { id: 'equipmentType', header: 'Type', accessor: 'equipmentType', dbField: 'equipment_type', width: 120, editable: true, filterable: true, type: 'text', showInDashboard: false },
    { id: 'id', header: 'ID', accessor: 'id', dbField: 'id', width: 50, editable: false, filterable: true, type: 'number', showInDashboard: false },
    { id: 'buildingNumber', header: 'Building Number', accessor: 'buildingNumber', dbField: 'building_number', width: 80, editable: true, filterable: true, type: 'text', showInDashboard: false },
    { id: 'sovCode', header: 'SOV Code', accessor: 'sovCode', dbField: 'sov_code', width: 80, editable: true, filterable: true, type: 'text', showInDashboard: false },
    { id: 'wbsCode', header: 'WBS Code', accessor: 'wbsCode', dbField: 'wbs_code', width: 80, editable: true, filterable: true, type: 'text', showInDashboard: false },
    { id: 'divisionArea', header: 'Division Area', accessor: 'divisionArea', dbField: 'division_area', width: 80, editable: true, filterable: true, type: 'text', showInDashboard: false },
    { id: 'subDivisionArea', header: 'Sub Division Area', accessor: 'subDivisionArea', dbField: 'sub_division_area', width: 80, editable: true, filterable: true, type: 'text', showInDashboard: false },
    { id: 'jobNumber', header: 'Job Number', accessor: 'jobNumber', dbField: 'job_number', width: 80, editable: false, filterable: true, type: 'text', showInDashboard: false },
    { id: 'activityId', header: 'Activity ID', accessor: 'activityId', dbField: 'activity_id', width: 130, editable: true, filterable: true, type: 'text', showInDashboard: true, headerGroup: 'Schedule' },
    { id: 'activityName', header: 'Activity Name', accessor: 'activityName', dbField: 'activity_name', width: 160, editable: true, filterable: true, type: 'text', showInDashboard: false, headerGroup: 'Schedule' },
    { id: 'budgetedHr', header: 'Budgeted Hr', accessor: 'budgetedHr', dbField: 'budgeted_hr', width: 80, editable: true, filterable: true, type: 'number', showInDashboard: false },
    { id: 'credit', header: 'Credit', accessor: 'credit', dbField: 'credit', width: 70, editable: true, filterable: true, type: 'number', showInDashboard: false },
    { id: 'earnedHr', header: 'Earned Hr', accessor: 'earnedHr', dbField: 'earned_hr', width: 80, editable: false, filterable: true, type: 'number', computed: true, showInDashboard: false },
    { id: 'earnedCredit', header: 'Earned Credit', accessor: 'earnedCredit', dbField: 'earned_credit', width: 90, editable: false, filterable: true, type: 'number', computed: true, showInDashboard: false },
    { id: 'costCode', header: 'Cost Code', accessor: 'costCode', dbField: 'cost_code', width: 80, editable: true, filterable: true, type: 'text', showInDashboard: false },
    { id: 'pAndId', header: 'P&ID', accessor: 'pAndId', dbField: 'p_and_id', width: 70, editable: true, filterable: true, type: 'text', showInDashboard: false },
    { id: 'createdAt', header: 'Created At', accessor: 'createdAt', dbField: 'created_at', width: 130, editable: false, filterable: true, type: 'datetime', showInDashboard: false },
    { id: 'updatedAt', header: 'Updated At', accessor: 'updatedAt', dbField: 'updated_at', width: 130, editable: false, filterable: true, type: 'datetime', showInDashboard: false },
  ],
  
  udfColumns: Array.from({ length: 10 }, (_, i) => ({
    id: `udf${String(i + 1).padStart(2, '0')}`,
    header: `UDF ${String(i + 1).padStart(2, '0')}`,
    accessor: `udf${String(i + 1).padStart(2, '0')}`,
    dbField: `udf${String(i + 1).padStart(2, '0')}`,
    width: 100,
    editable: true,
    filterable: true,
    type: 'text',
    isUdf: true,
    showInDashboard: false,
  })),
  
  fieldMap: FIELD_MAP,
  editableColumns: EDITABLE_COLUMNS,
  
  transformData: transformMatrixData,
  buildChangePayload: buildChangePayload,
  
  features: {
    dragFill: true,
    progressTrends: true,
    matBore: false,
    rulesOfCredit: true,
    columnConfig: true,
    actualSpentUpload: false,
    export: true,
    upload: true,
    presence: true,
    budgetReport: false,
    earnedReport: false,
    evmReport: false,
  },
};

export default skidsConfig;
